import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import type { Challenge, Completion, TaskAssignment } from "@starquest/core";
import styles from "./KidHome.module.css";
import PreviewBanner from "../components/PreviewBanner";
import ThemeWrapper from "../components/ThemeWrapper";
import { useTheme } from "../hooks/useTheme";

type Tab = "quests" | "tasks";

const TIER_LABEL: Record<string, string> = {
  early:  "🌟 Early bird",
  ontime: "✅ On time",
  late:   "⏱️ Late",
};

export default function Codex() {
  const navigate     = useNavigate();
  const { familyId: authFamilyId } = useAuthStore();
  const { selectedKid, challenges, setChallenges } = useFamilyStore();
  const [completions,   setCompletions]   = useState<Completion[]>([]);
  const [taskHistory,   setTaskHistory]   = useState<TaskAssignment[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [tab,           setTab]           = useState<Tab>("quests");

  const kid = selectedKid;
  const familyId = authFamilyId || kid?.familyId || null;
  const theme = useTheme(selectedKid);

  useEffect(() => {
    if (!selectedKid) navigate("/kid");
  }, [selectedKid, navigate]);

  useEffect(() => {
    if (!familyId || !kid) return;
    setLoading(true);
    Promise.all([
      api.listChallenges(familyId).then((c) => setChallenges(c as Challenge[])),
      api.listCompletions(familyId).then((c) =>
        setCompletions((c as Completion[]).filter((x) => x.status === "approved" && x.kidId === kid.kidId))
      ),
      api.listTaskAssignments(familyId, { kidId: kid.kidId }).then((a) => {
        const approved = (a as TaskAssignment[])
          .filter((x) => x.status === "approved")
          .sort((a, b) => new Date(b.reviewedAt ?? b.dueDate).getTime() - new Date(a.reviewedAt ?? a.dueDate).getTime());
        setTaskHistory(approved);
      }),
    ]).finally(() => setLoading(false));
  }, [familyId, kid?.kidId]);

  function challengeNameFor(comp: Completion) {
    return challenges.find((c) => c.challengeId === comp.challengeId)?.challengeName ?? `Challenge #${comp.completionId.slice(0, 6)}`;
  }
  function challengeEmojiFor(comp: Completion) {
    return challenges.find((c) => c.challengeId === comp.challengeId)?.emoji ?? "⭐";
  }
  function challengePointReward(comp: Completion) {
    return challenges.find((c) => c.challengeId === comp.challengeId)?.pointReward ?? 0;
  }

  if (!kid) return (
    <div className={styles.pickerPage}>
      <div className={styles.pickerStars} aria-hidden />
      <div style={{ color: "rgba(240,240,255,0.5)", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📖</div>
        <div>{`Select a ${theme.kidRole.toLowerCase()} first`}</div>
        <button onClick={() => navigate("/kid")} style={{ marginTop: 16, padding: "10px 20px", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 999, color: "var(--star)", cursor: "pointer", fontFamily: "var(--font-display)", fontWeight: 700 }}>← Back</button>
      </div>
    </div>
  );

  return (
    <ThemeWrapper kid={selectedKid}>
    <div className={styles.page}>
      <PreviewBanner />
      <div className={styles.starbg} aria-hidden />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", position: "relative", zIndex: 2 }}>
        <button className={styles.hudBack} onClick={() => navigate("/kid")}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18 L9 12 L15 6"/></svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 22 }}>📖 {theme.challengeLogTitle}</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(240,240,255,0.5)", marginTop: 2 }}>
            {kid.name.toUpperCase()} · {completions.length} quests · {taskHistory.length} tasks
          </div>
        </div>
        <div className={styles.starBal} style={{ flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--gold)"><path d="M12 2 L14.6 9.4 L22 10 L16.3 14.8 L18.2 22 L12 17.8 L5.8 22 L7.7 14.8 L2 10 L9.4 9.4 Z"/></svg>
          {kid.pointBalance}
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 6, position: "relative", zIndex: 2, background: "rgba(255,255,255,0.04)", padding: 4, borderRadius: 12, width: "fit-content", border: "1px solid rgba(255,255,255,0.08)" }}>
        {(["quests", "tasks"] as Tab[]).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={{
            padding: "7px 18px", borderRadius: 8, border: "none", cursor: "pointer",
            fontFamily: "var(--font-display)", fontWeight: tab === t ? 900 : 700, fontSize: 13,
            background: tab === t ? "var(--gold)" : "transparent",
            color: tab === t ? "var(--deep)" : "rgba(240,240,255,0.6)",
          }}>
            {t === "quests" ? `⚔️ ${theme.challengeNamePlural}` : "📋 Tasks"}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", position: "relative", zIndex: 2, display: "flex", flexDirection: "column", gap: 8 }}>
        {loading && (
          <div style={{ color: "rgba(240,240,255,0.4)", textAlign: "center", padding: 40 }}>Loading history…</div>
        )}

        {/* Quests tab */}
        {!loading && tab === "quests" && (
          <>
            {completions.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "60px 0", textAlign: "center" }}>
                <div style={{ fontSize: 64 }}>🗺️</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 20 }}>No quests completed yet</div>
                <div style={{ color: "rgba(240,240,255,0.5)", fontSize: 14 }}>Find stars hidden around your home!</div>
              </div>
            ) : (
              completions.map((comp) => (
                <div key={comp.completionId} style={{
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 16, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14,
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,rgba(255,215,0,0.2),rgba(106,13,173,0.2))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                    {challengeEmojiFor(comp)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15 }}>{challengeNameFor(comp)}</div>
                    <div style={{ fontSize: 12, color: "rgba(240,240,255,0.5)", marginTop: 3 }}>
                      {new Date(comp.reviewedAt ?? comp.submittedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, color: "var(--gold)", fontSize: 18 }}>{`+${challengePointReward(comp)} ${theme.pointEmoji}`}</div>
                </div>
              ))
            )}
          </>
        )}

        {/* Tasks tab */}
        {!loading && tab === "tasks" && (
          <>
            {taskHistory.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "60px 0", textAlign: "center" }}>
                <div style={{ fontSize: 64 }}>📋</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 20 }}>No tasks completed yet</div>
                <div style={{ color: "rgba(240,240,255,0.5)", fontSize: 14 }}>Complete your daily tasks to see them here!</div>
              </div>
            ) : (
              taskHistory.map((a) => (
                <div key={a.assignmentId} style={{
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 16, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14,
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>
                    {a.taskEmoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15 }}>{a.taskName}</div>
                    <div style={{ fontSize: 12, color: "rgba(240,240,255,0.5)", marginTop: 3, display: "flex", gap: 8, alignItems: "center" }}>
                      <span>{new Date(a.reviewedAt ?? a.dueDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                      {a.tier && <span style={{ color: a.tier === "early" ? "#34d399" : a.tier === "late" ? "#f87171" : "#fbbf24", fontSize: 11 }}>{TIER_LABEL[a.tier]}</span>}
                    </div>
                  </div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, color: "#34d399", fontSize: 18 }}>+{theme.pointEmoji} {a.pointsAwarded ?? 0}</div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      <nav className={styles.nav}>
        <div className={styles.navBtn} onClick={() => navigate("/kid")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 11 L12 3 L21 11"/><path d="M5 10 V21 H19 V10"/></svg>
          Home
        </div>
        <div className={`${styles.navBtn} ${styles.navActive}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9 H21"/></svg>
          {theme.challengeLogTitle}
        </div>
        <div className={styles.navBtn} onClick={() => navigate("/kid/cashout")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 21 L21 21"/><path d="M3 10 L12 3 L21 10"/></svg>
          {theme.pointBankTitle}
        </div>
        <div className={styles.navBtn} onClick={() => navigate("/kid/store")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 7 L21 7 L20 20 L4 20 Z"/><path d="M9 7 V5 A3 3 0 0 1 15 5 V7"/></svg>
          Store
        </div>
      </nav>
    </div>
    </ThemeWrapper>
  );
}
